/**
 * Parser for wafer map .txt files
 * 
 * Generic format expectations:
 * - Metadata/header lines
 * - PATTERN GRID marker
 * - # (start separator)
 * - Wafer map rows (each character represents a die)
 * - ## (end separator)
 * - Additional metadata/footer lines
 */

export type DieType = 'Cropped' | 'Partial' | 'ProbedDie';

export interface WaferMapParserResult {
  uniqueCharacters: string[];
  allCharacters: Array<{
    char: string;
    x: string;
    y: string;
    dieType: DieType;
  }>;
  metadata: {
    lotId?: string;
    waferId?: string;
    referenceDie?: string;
    rows?: number;
    columns?: number;
    productId?: string;
    checksum?: string;
    probeDate?: string;
    probeTime?: string;
    convDate?: string;
    convTime?: string;
    dieProbed?: number;
    skipDie?: number;
    inkedDie?: number;
    bins?: Array<{ bin: number; count: number }>;
    waferSize?: number;
    flatProbed?: number;
    ySize?: number;
    xSize?: number;
    [key: string]: any;
  };
}

/**
 * Maps character to die type based on character value
 */
const getDieTypeFromChar = (char: string): DieType => {
  if (char === '.' || char === ' ') {
    return 'Cropped';
  } else if (char === 'p' || char === 'o') {
    return 'Partial';
  } else {
    return 'ProbedDie';
  }
};

/**
 * Parses a wafer map .txt file and extracts:
 * 1. Unique characters (including spaces and dots)
 * 2. All characters with their X,Y coordinates and dieType property
 * 3. Metadata information
 */
export const parseWaferMapFile = (fileContent: string): WaferMapParserResult => {
  const lines = fileContent.split(/\r?\n/);
  
  // Extract unique characters and all characters with coordinates
  const uniqueCharsSet = new Set<string>();
  const allCharacters: Array<{ char: string; x: string; y: string; dieType: DieType }> = [];
  
  // Find the wafer map section using generic markers:
  // PATTERN GRID -> next # -> ##.
  let mapStartIndex = -1;
  let mapEndIndex = -1;
  const waferLines: string[] = [];

  const patternGridIndex = lines.findIndex((line) => line.trim().toUpperCase() === 'PATTERN GRID');
  if (patternGridIndex !== -1) {
    for (let i = patternGridIndex + 1; i < lines.length; i++) {
      if (lines[i].trim() === '#') {
        mapStartIndex = i + 1;
        break;
      }
    }
  }

  if (mapStartIndex !== -1) {
    for (let i = mapStartIndex; i < lines.length; i++) {
      if (lines[i].trim() === '##') {
        mapEndIndex = i;
        break;
      }
    }
  }

  if (mapStartIndex !== -1 && mapEndIndex !== -1) {
    for (let i = mapStartIndex; i < mapEndIndex; i++) {
      waferLines.push(lines[i]);
    }
  }
  
  // Parse wafer map characters (including spaces)
  let rowIndex = 0;
  for (const line of waferLines) {
    for (let colIndex = 0; colIndex < line.length; colIndex++) {
      const char = line[colIndex];
      // Include all characters including spaces
      uniqueCharsSet.add(char);
      allCharacters.push({
        char: char,
        x: colIndex.toString(),
        y: rowIndex.toString(),
        dieType: getDieTypeFromChar(char),
      });
    }
    rowIndex++;
  }
  
  // Parse metadata
  const metadata: any = {};
  
  // Parse metadata using case-insensitive generic rules
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const normalizedLine = line.trim();
    const upperLine = normalizedLine.toUpperCase();

    if (upperLine.startsWith('LOT ID') || upperLine.startsWith('WAFER_LOT_ID:')) {
      metadata.lotId = normalizedLine.replace(/^(LOT ID|WAFER_LOT_ID:)\s*/i, '').trim();
    } else if (upperLine.startsWith('WAFER ID') || upperLine.startsWith('WAFER_ID:')) {
      metadata.waferId = normalizedLine.replace(/^(WAFER ID|WAFER_ID:)\s*/i, '').trim();
    } else if (upperLine.startsWith('REFERENCE DIE') || upperLine.startsWith('REFRENCE DIE')) {
      metadata.referenceDie = normalizedLine.replace(/^(REFERENCE DIE|REFRENCE DIE)\s*/i, '').trim();
    } else if (upperLine.startsWith('ROWS')) {
      const rowsMatch = normalizedLine.match(/ROWS\s*[:\t ]\s*(\d+)/i);
      if (rowsMatch) {
        metadata.rows = parseInt(rowsMatch[1], 10);
      }
    } else if (upperLine.startsWith('NUMBER_OF_ROW:')) {
      const rowsMatch = normalizedLine.match(/NUMBER_OF_ROW:\s*(\d+)/i);
      if (rowsMatch) {
        metadata.rows = parseInt(rowsMatch[1], 10);
      }
    } else if (upperLine.startsWith('COLUMNS')) {
      const colsMatch = normalizedLine.match(/COLUMNS\s*[:\t ]\s*(\d+)/i);
      if (colsMatch) {
        metadata.columns = parseInt(colsMatch[1], 10);
      }
    } else if (upperLine.startsWith('NUMBER_OF_COLUMN:')) {
      const colsMatch = normalizedLine.match(/NUMBER_OF_COLUMN:\s*(\d+)/i);
      if (colsMatch) {
        metadata.columns = parseInt(colsMatch[1], 10);
      }
    } else if (upperLine.startsWith('PRODUCT ID') || upperLine.startsWith('PRODUCT_ID:')) {
      metadata.productId = normalizedLine.replace(/^(PRODUCT ID|PRODUCT_ID:)\s*/i, '').trim();
    }
    
    // Common metadata fields (all formats)
    if (upperLine.startsWith('CHECKSUM')) {
      metadata.checksum = normalizedLine.replace(/^CHECKSUM\s*/i, '').trim().replace(/\t/g, ' ').trim();
    } else if (upperLine.startsWith('PROBE DATE')) {
      metadata.probeDate = normalizedLine.replace(/^PROBE DATE\s*/i, '').trim();
    } else if (upperLine.startsWith('PROBE TIME')) {
      metadata.probeTime = normalizedLine.replace(/^PROBE TIME\s*/i, '').trim();
    } else if (upperLine.startsWith('CONV DATE') || upperLine.startsWith('CONV  DATE')) {
      metadata.convDate = normalizedLine.replace(/CONV\s+DATE/i, '').trim();
    } else if (upperLine.startsWith('CONV TIME') || upperLine.startsWith('CONV  TIME')) {
      metadata.convTime = normalizedLine.replace(/CONV\s+TIME/i, '').trim();
    } else if (upperLine.startsWith('DIE PROBED')) {
      const match = normalizedLine.match(/DIE PROBED\s+(\d+)/i);
      if (match) {
        metadata.dieProbed = parseInt(match[1], 10);
      }
    } else if (upperLine.startsWith('SKIP DIE')) {
      const match = normalizedLine.match(/SKIP DIE\s+(\d+)/i);
      if (match) {
        metadata.skipDie = parseInt(match[1], 10);
      }
    } else if (upperLine.startsWith('INKED DIE')) {
      const match = normalizedLine.match(/INKED DIE\s+(\d+)/i);
      if (match) {
        metadata.inkedDie = parseInt(match[1], 10);
      }
    } else if (upperLine.startsWith('BIN')) {
      const match = normalizedLine.match(/BIN\s+(\d+)\s+(\d+)/i);
      if (match) {
        if (!metadata.bins) {
          metadata.bins = [];
        }
        metadata.bins.push({
          bin: parseInt(match[1], 10),
          count: parseInt(match[2], 10),
        });
      }
    } else if (upperLine.includes('WAFER SIZE')) {
      // Handle various formats: "WAFER SIZE 152.4", "WAFER SIZE\t152.4", etc.
      const match = normalizedLine.match(/WAFER\s+SIZE\s*[:\t]?\s*([\d.]+)/i);
      if (match) {
        metadata.waferSize = parseFloat(match[1]);
      }
    } else if (upperLine.startsWith('FLAT PROBED')) {
      const match = normalizedLine.match(/FLAT PROBED\s*[:\t ]?\s*(\d+)/i);
      if (match) {
        metadata.flatProbed = parseInt(match[1], 10);
      } else {
        // Handle case where there's no space (FLAT PROBED90)
        const altMatch = normalizedLine.match(/FLAT PROBED(\d+)/i);
        if (altMatch) {
          metadata.flatProbed = parseInt(altMatch[1], 10);
        }
      }
    } else if (upperLine.startsWith('Y SIZE')) {
      const match = normalizedLine.match(/Y SIZE\s*[:\t ]\s*([\d.]+)/i);
      if (match) {
        metadata.ySize = parseFloat(match[1]);
      }
    } else if (upperLine.startsWith('X SIZE')) {
      const match = normalizedLine.match(/X SIZE\s*[:\t ]\s*([\d.]+)/i);
      if (match) {
        metadata.xSize = parseFloat(match[1]);
      }
    } else if (upperLine.startsWith('FLAT_NOTCH_LOCATION:')) {
      const flatNotch = normalizedLine.replace('FLAT_NOTCH_LOCATION:', '').trim().toUpperCase();
      // Map flat positions to numeric values
      // 0=BOTTOM/DOWN, 90=RIGHT, 180=TOP/UP, 270=LEFT
      if (flatNotch.includes('BOTTOM') || flatNotch.includes('DOWN') || flatNotch.includes('6 O\'CLOCK')) {
        metadata.flatProbed = 0;
      } else if (flatNotch.includes('RIGHT') || flatNotch.includes('3 O\'CLOCK')) {
        metadata.flatProbed = 90;
      } else if (flatNotch.includes('TOP') || flatNotch.includes('UP') || flatNotch.includes('12 O\'CLOCK')) {
        metadata.flatProbed = 180;
      } else if (flatNotch.includes('LEFT') || flatNotch.includes('9 O\'CLOCK')) {
        metadata.flatProbed = 270;
      }
    }
  }
  
  const result: WaferMapParserResult = {
    uniqueCharacters: Array.from(uniqueCharsSet).sort(),
    allCharacters,
    metadata,
  };
  
  return result;
};

